import React, { useState } from "react";
import type { NoteName } from "../../lib/guitarEngine";

export interface ExploreScaleHeroProps {
  rootNote: NoteName;
  scaleLabel: string;
  scaleType: string;
  notes: readonly NoteName[];
  scaleOptions: readonly { value: string; label: string }[];
  noteCount: number;
  degreeCount: number;
  onPrevNote: () => void;
  onNextNote: () => void;
  onPrevScale: () => void;
  onNextScale: () => void;
  onSelectNote: (n: NoteName) => void;
  onSelectScale: (value: string) => void;
  /** Vista Explore sandbox: título más pequeño, menos margen */
  compact?: boolean;
  /** Lienzo inmersivo edge-to-edge: título masivo text-8xl */
  immersive?: boolean;
}

export const ExploreScaleHero: React.FC<ExploreScaleHeroProps> = ({
  rootNote,
  scaleLabel,
  scaleType,
  notes,
  scaleOptions,
  noteCount,
  degreeCount,
  onPrevNote,
  onNextNote,
  onPrevScale,
  onNextScale,
  onSelectNote,
  onSelectScale,
  compact = false,
  immersive = false,
}) => {
  const [noteOpen, setNoteOpen] = useState(false);
  const [scaleOpen, setScaleOpen] = useState(false);

  return (
    <div
      className={`relative shrink-0 text-left ${
        immersive ? "pb-0" : compact ? "pb-0 mb-2" : "pb-4 pt-1"
      }`}
    >
      {(noteOpen || scaleOpen) && (
        <button
          type="button"
          className="fixed inset-0 z-40 cursor-default"
          aria-label="Cerrar selector"
          onClick={() => {
            setNoteOpen(false);
            setScaleOpen(false);
          }}
        />
      )}

      <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1">
        <button
          type="button"
          onClick={() => {
            setNoteOpen((o) => !o);
            setScaleOpen(false);
          }}
          className={`font-serif font-light leading-none text-white hover:text-emerald-300 transition-colors ${
            immersive
              ? "text-8xl"
              : compact
              ? "text-[clamp(2.25rem,5vw,3.75rem)]"
              : "text-[clamp(3rem,8vw,5.5rem)]"
          }`}
          title="Cambiar tónica"
        >
          {rootNote}
        </button>
        <button
          type="button"
          onClick={() => {
            setScaleOpen((o) => !o);
            setNoteOpen(false);
          }}
          className={`font-serif font-light leading-none text-white hover:text-purple-300 transition-colors ${
            immersive
              ? "text-8xl"
              : compact
              ? "text-[clamp(2.25rem,5vw,3.75rem)]"
              : "text-[clamp(3rem,8vw,5.5rem)]"
          }`}
          title="Cambiar escala"
        >
          {scaleLabel}
        </button>
      </div>

      {!immersive && (
      <p className={`font-sans text-white/40 tabular-nums ${compact ? "mt-0.5 text-[10px]" : "mt-2 text-[11px]"}`}>
        {noteCount} notas · {degreeCount} grados
      </p>
      )}

      {noteOpen && (
        <div className="absolute left-0 top-full mt-2 z-50 grid grid-cols-6 gap-1 p-2">
          {notes.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => {
                onSelectNote(n);
                setNoteOpen(false);
              }}
              className={`h-9 font-sans text-sm transition-colors ${
                rootNote === n
                  ? "text-emerald-300 font-semibold"
                  : "text-white/50 hover:text-white/80"
              }`}
            >
              {n}
            </button>
          ))}
        </div>
      )}

      {scaleOpen && (
        <div className="absolute left-0 top-full mt-2 z-50 flex flex-col gap-0.5 min-w-[10rem]">
          {scaleOptions.map((s) => (
            <button
              key={s.value}
              type="button"
              onClick={() => {
                onSelectScale(s.value);
                setScaleOpen(false);
              }}
              className={`text-left py-1.5 font-sans text-sm transition-colors ${
                scaleType === s.value
                  ? "text-purple-300 font-semibold"
                  : "text-white/50 hover:text-white/80"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      <div className="sr-only">
        <button type="button" onClick={onPrevNote}>Nota anterior</button>
        <button type="button" onClick={onNextNote}>Nota siguiente</button>
        <button type="button" onClick={onPrevScale}>Escala anterior</button>
        <button type="button" onClick={onNextScale}>Escala siguiente</button>
      </div>
    </div>
  );
};
